import { useMutationEditLead, useMutationLeadStatus } from "@gymflow/api";
import { FormikProvider, useFormik } from "formik";
import { useQueryLead } from "libs/api/src/lib/hooks/lead/useQueryLead";
import { useTranslation } from "react-i18next";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import useGymflowModels from "../../../store";
import { Button, FormInput } from "../../atoms";
import { FormSelectLeadSource } from "../../atoms/form/selects/components/FormSelectLeadSource";
import { FormSelectLeadStatus } from "../../atoms/form/selects/components/FormSelectLeadStatus";
import { FormPhoneNumberInput } from "../../atoms/form/text/FormPhoneNumberInput";
import { LeadFormSchema } from "./LeadFormSchema";

export const LeadForm = ({ leadId }: { leadId: number }) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { data: lead } = useQueryLead({ api, leadId });
  const { mutateAsync: updateLead } = useMutationEditLead({ api });
  const { changeLeadStatusMutation: updateLeadStatus } = useMutationLeadStatus({
    api,
  });
  const formik = useFormik({
    initialValues: {
      firstName: lead!.firstName,
      lastName: lead!.lastName,
      mobileNumber: lead!.mobileNumber,
      leadStatus: {
        id: lead!.leadStatus.id,
        label: lead!.leadStatus.name,
        value: lead!.leadStatus,
      },
      source: {
        id: lead!.source.id,
        label: lead!.source.name,
        value: lead!.source,
      },
    },
    validationSchema: toFormikValidationSchema(LeadFormSchema),
    validate: toFormikValidate(LeadFormSchema),
    onSubmit: async (values) => {
      if (values.leadStatus.value.id !== lead!.leadStatus.id) {
        await updateLeadStatus.mutateAsync({
          leadId: leadId,
          newColumn: values.leadStatus.value.id,
        });
      }
      await updateLead({
        id: leadId,
        patchedFields: {
          firstName: values.firstName,
          lastName: values.lastName,
          mobileNumber: values.mobileNumber,
          sourceId: values.source.value.id,
        },
      });
    },
  });
  return (
    <FormikProvider value={formik}>
      <div className="flex w-full flex-col">
        <div className="grid w-full grid-cols-1 gap-4 p-6 lg:grid-cols-2">
          <FormInput
            name="firstName"
            placeholder={t("pages.lead.profile.firstName")}
            label={t("pages.lead.profile.firstName")}
          />

          <FormInput
            name="lastName"
            placeholder={t("pages.lead.profile.lastName")}
            label={t("pages.lead.profile.lastName")}
          />
          <FormPhoneNumberInput
            name="mobileNumber"
            placeholder={t("pages.lead.profile.mobileNumber")}
            label={t("pages.lead.profile.mobileNumber")}
          />

          <FormSelectLeadStatus name="leadStatus" />
          <FormSelectLeadSource name="source" />
        </div>

        <div className="border-b border-gray-200" />
        <div className="flex w-full flex-row justify-end gap-4 p-6">
          <Button
            intent="secondary"
            disabled={!formik.dirty}
            onClick={async () => {
              await formik.submitForm();
            }}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
